/**
 * 브라우저 dev 용 구독 목 — **네트워크를 쓰지 않는다.**
 *
 * 왜 MSW 가 아니라 이것인가: MSW 브라우저 모드는 Service Worker 등록이 필요해서
 * `mockServiceWorker.js` 를 앱 4개에 심어야 하고, 그러면 **REST 까지 목 계층을 통과**한다.
 * 우리는 REST 를 실물로 때려야 하므로(설계안 §6) 브라우저에서는 **연결 팩토리만** 갈아끼운다.
 * MSW 핸들러(`handlers.ts`)는 테스트에서 진짜 `WebSocket` 코드 경로를 검증하는 데 쓴다.
 * **프레임 생성기(`feed.ts`)는 둘이 공유**하므로 두 목이 서로 다른 모양을 내지 않는다.
 *
 * ⚠ 타입을 `@ctcells/realtime` 에서 가져오지 않고 구조만 맞춘다 — fixtures 가 realtime 에
 *   딸려가지 않게 하려는 것이다(`DemoRealtimeState` 와 같은 태도).
 */
import { DEFAULT_SCENARIO, DEMO_SCENARIOS, type DemoScenarioKey } from '../scenarios.ts'
import { createStreamFeed, safetyFrame, type StreamFeed } from './feed.ts'

export type DemoStreamName = 'events' | 'safety'
export type DemoStreamStatus = 'idle' | 'connecting' | 'open' | 'closed'

export interface DemoStreamConnectionOptions {
  name: DemoStreamName
  url: string
  onStatus: (status: DemoStreamStatus) => void
  onMessage: (raw: unknown) => void
}

export interface DemoStreamConnection {
  readonly name: DemoStreamName
  close(): void
}

export interface DemoStreamControls {
  /** 🔴 연결 끊김 → 재연결 재현 */
  drop(): void
  /** 장기 단절. `resume()` 전까지 재연결이 실패한다 */
  goOffline(): void
  /** 🔴 소켓은 열린 채 메시지만 끊는다 — stale 축 재현 */
  silence(): void
  resume(): void
  setScenario(key: DemoScenarioKey): void
  emitSafety(type: Parameters<typeof safetyFrame>[0]): void
  /** 현재 상태 한 줄(콘솔에서 확인용) */
  status(): { offline: boolean; silent: boolean; connections: number; scenario: DemoScenarioKey }
}

export interface DemoStreamFactoryOptions {
  scenario?: DemoScenarioKey
  /** 주기 프레임 간격(ms). ⚠ 계약의 heartbeat 간격이 아니다 */
  tickMs?: number
  /** 연결 수립까지 걸리는 시간(ms) — 로딩 경계를 눈으로 보려고 둔다 */
  connectDelayMs?: number
  /** 끊긴 뒤 재연결까지(ms) */
  reconnectDelayMs?: number
}

const DEFAULTS = { tickMs: 1_000, connectDelayMs: 150, reconnectDelayMs: 1_000 } as const

export function createDemoStreamFactory(options: DemoStreamFactoryOptions = {}): {
  factory: (options: DemoStreamConnectionOptions) => DemoStreamConnection
  controls: DemoStreamControls
} {
  const tickMs = options.tickMs ?? DEFAULTS.tickMs
  const connectDelayMs = options.connectDelayMs ?? DEFAULTS.connectDelayMs
  const reconnectDelayMs = options.reconnectDelayMs ?? DEFAULTS.reconnectDelayMs

  let scenarioKey: DemoScenarioKey = options.scenario ?? DEFAULT_SCENARIO.key
  let feed: StreamFeed = createStreamFeed(DEMO_SCENARIOS[scenarioKey])
  let offline = false
  let silent = false

  interface Session {
    name: DemoStreamName
    onStatus: (status: DemoStreamStatus) => void
    onMessage: (raw: unknown) => void
    tick: ReturnType<typeof setInterval> | null
    pending: ReturnType<typeof setTimeout> | null
    open: boolean
    disposed: boolean
  }

  const sessions = new Set<Session>()

  function stopTick(session: Session): void {
    if (session.tick !== null) {
      clearInterval(session.tick)
      session.tick = null
    }
  }

  function connect(session: Session): void {
    if (session.disposed) return
    session.onStatus('connecting')
    session.pending = setTimeout(() => {
      session.pending = null
      if (session.disposed) return
      if (offline) {
        session.open = false
        session.onStatus('closed')
        scheduleReconnect(session)
        return
      }
      session.open = true
      session.onStatus('open')

      // 안전 채널은 트리거가 있을 때만 흘린다 — 주기 발행을 지어내지 않는다
      if (session.name !== 'events') return

      for (const frame of feed.hello()) session.onMessage(frame)
      session.tick = setInterval(() => {
        if (silent || offline || !session.open) return
        session.onMessage(feed.next())
      }, tickMs)
    }, connectDelayMs)
  }

  function disconnect(session: Session): void {
    stopTick(session)
    if (!session.open) return
    session.open = false
    session.onStatus('closed')
    scheduleReconnect(session)
  }

  function scheduleReconnect(session: Session): void {
    if (session.disposed || session.pending !== null) return
    session.pending = setTimeout(() => {
      session.pending = null
      connect(session)
    }, reconnectDelayMs)
  }

  return {
    factory(connectionOptions) {
      const session: Session = {
        name: connectionOptions.name,
        onStatus: connectionOptions.onStatus,
        onMessage: connectionOptions.onMessage,
        tick: null,
        pending: null,
        open: false,
        disposed: false,
      }
      sessions.add(session)
      connect(session)

      return {
        name: session.name,
        close() {
          session.disposed = true
          stopTick(session)
          if (session.pending !== null) {
            clearTimeout(session.pending)
            session.pending = null
          }
          session.open = false
          sessions.delete(session)
          session.onStatus('closed')
        },
      }
    },
    controls: {
      drop() {
        for (const session of sessions) disconnect(session)
      },
      goOffline() {
        offline = true
        for (const session of sessions) disconnect(session)
      },
      silence() {
        silent = true
      },
      resume() {
        offline = false
        silent = false
      },
      setScenario(key) {
        scenarioKey = key
        feed = createStreamFeed(DEMO_SCENARIOS[key])
      },
      emitSafety(type) {
        const frame = safetyFrame(type)
        for (const session of sessions) {
          if (session.name === 'safety' && session.open) session.onMessage(frame)
        }
      },
      status() {
        return { offline, silent, connections: sessions.size, scenario: scenarioKey }
      },
    },
  }
}

/**
 * 개발 중 손으로 끊김·재연결·stale 을 재현하려고 전역에 붙인다.
 * ⚠ dev 에서만 부른다(호출부가 `import.meta.env.DEV` 로 가른다).
 *
 * ```js
 * __CTCELLS_STREAM_MOCK__.drop()        // 끊김 → 자동 재연결
 * __CTCELLS_STREAM_MOCK__.goOffline()   // 계속 끊긴 상태
 * __CTCELLS_STREAM_MOCK__.silence()     // 소켓은 열린 채 메시지만 끊김
 * __CTCELLS_STREAM_MOCK__.setScenario('ERROR')
 * ```
 */
export function exposeDemoStreamControls(controls: DemoStreamControls): void {
  ;(globalThis as { __CTCELLS_STREAM_MOCK__?: DemoStreamControls }).__CTCELLS_STREAM_MOCK__ =
    controls
}
